import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { membershipService, clubService } from '../services/apiClient';
import { AuthContext } from '../context/AuthContext';
import Navbar from '../components/Navbar';

const MembershipPage = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialClubId = queryParams.get('club') || '';

  const [clubs, setClubs] = useState([]);
  const [clubId, setClubId] = useState(initialClubId);
  const [subSociety, setSubSociety] = useState('');
  const [message, setMessage] = useState('');
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchClubs();
    fetchMemberships();
  }, []);

  useEffect(() => {
    if (initialClubId) setClubId(initialClubId);
  }, [initialClubId]);

  const fetchClubs = async () => {
    try {
      const response = await clubService.getAllClubs();
      setClubs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch clubs:', error);
    }
  };

  const fetchMemberships = async () => {
    try {
      const response = await membershipService.getUserMemberships();
      setMemberships(response.data || []);
    } catch (error) {
      console.error('Failed to fetch memberships:', error);
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await membershipService.applyForMembership({ clubId, subSociety, message });
      setSubmitted(true);
      setClubId('');
      setSubSociety('');
      setMessage('');
      setTimeout(() => setSubmitted(false), 3000);
      fetchMemberships();
    } catch (error) {
      alert('Failed to apply: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const selectedClub = clubs.find(c => c._id === clubId);
  const isIEEE = selectedClub?.name === 'IEEE';

  const ieeeSubSocieties = [
    'Computer Society',
    'Robotics & Automation Society (RAS)',
    'Power & Energy Society (PES)',
    'Women in Engineering (WIE)',
    'Signal Processing Society',
    'Communication Society'
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="bg-purple-600 px-8 py-6">
            <h1 className="text-3xl font-bold text-white">Club Membership</h1>
            <p className="text-purple-100 mt-2">Join a community and start making an impact.</p>
          </div>

          <div className="p-8">
            {submitted && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
                <span className="text-xl">✓</span>
                Application submitted successfully! It is now pending approval.
              </div>
            )}

            <form onSubmit={handleApply} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Select Club</label>
                  <select
                    value={clubId}
                    onChange={(e) => {
                      setClubId(e.target.value);
                      setSubSociety('');
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                    required
                  >
                    <option value="">-- Choose a club --</option>
                    {clubs.map(c => (
                      <option key={c._id} value={c._id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                {isIEEE && (
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Sub-Society</label>
                    <select
                      value={subSociety}
                      onChange={(e) => setSubSociety(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                    >
                      <option value="">General Member</option>
                      {ieeeSubSocieties.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Why do you want to join? (Motivation)</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell the club admin why you are interested in joining..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none h-32 resize-none"
                  required
                ></textarea>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-sm font-bold text-blue-800 mb-2">Notice:</h3>
                <p className="text-sm text-blue-700 leading-relaxed">
                  Upon submission, your application will be sent to the Club Admin. You will receive a notification once your membership is reviewed.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || !clubId}
                className="w-full bg-purple-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-purple-700 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting Application...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
            <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-sm">📋</span>
            Application History
          </h2>

          {memberships.length > 0 ? (
            <div className="space-y-4">
              {memberships.map((m) => (
                <div key={m._id} className="border border-gray-100 p-6 rounded-xl bg-gray-50 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">{m.club?.name || 'Unknown Club'}</h3>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {m.subSociety && (
                        <span className="text-sm bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-100">
                          {m.subSociety}
                        </span>
                      )}
                      <span className="text-xs text-gray-400 font-medium">Applied on: {new Date(m.appliedDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${m.status === 'Approved' ? 'bg-green-100 text-green-700' :
                      m.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                      {m.status}
                    </span>
                    {m.status === 'Rejected' && m.rejectionReason && (
                      <p className="text-xs text-red-500 mt-2 italic max-w-xs text-right">Reason: {m.rejectionReason}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border-2 border-dashed border-gray-100 rounded-xl">
              <p className="text-gray-400">You haven't applied to any clubs yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MembershipPage;
